<script setup lang="ts">
import { breakpointsTailwind } from '@vueuse/core'
import { type Elemental, ElementalTypeEnum } from '~~/src/types/elemental'
import { GALLERY_SIZES, PROMPT_TYPES } from '~~/src/utils/constants'
import { formatThousands, formatTimeAgo } from '@/utils/formatting'
import { useTopicsQuery } from '~~/src/composables/query/topic/useTopicsQuery'
interface IProps {
  prompt: Elemental
  variant?: PromptVariant
  showFeaturedTag?: boolean
  showUpvoteButton?: boolean
  lastItem?: boolean
}

const props = withDefaults(
  defineProps<IProps>(),
  {
    variant: 'default',
    showFeaturedTag: true,
    showUpvoteButton: true,
  },
)
const { prompt, variant, showFeaturedTag, showUpvoteButton, lastItem } = toRefs(props)
const { colors } = useTheme()

const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = breakpoints.smallerOrEqual('sm')
const isList = computed(() => prompt.value.isList)

const promptImages = computed(() => (prompt.value).images?.output)
const promptImageSizes = computed(() => promptImages.value?.length ? GALLERY_SIZES[promptImages.value?.length] : null)

const avatarSize = computed(() => {
  return isMobile.value ? 40 : 60
})

const containerRef = ref<HTMLElement>()
const { data: topics } = useTopicsQuery({
  query: {},
})
const savePopperShown = ref(false)

const elementalType = computed(() => useElementalTypes().value.find(t => prompt.value.isList ? t.id === ElementalTypeEnum.List : t.id === prompt.value.elementalTypeId))

const optimizedPromptAvatar = computed(() => {
  if (!prompt.value.isPremium)
    return null

  if (!prompt.value.avatar)
    return '/prompt_avatar.png'

  return optimizeImage(prompt.value.avatar, {
    width: avatarSize.value * 2, height: avatarSize.value * 2,
  })
})

function handleSavePopperToggle(show: boolean) {
  savePopperShown.value = show
}
</script>

<script lang="ts">
// eslint-disable-next-line @typescript-eslint/semi
export type PromptVariant = 'default' | 'small' | 'in-list';
</script>

<template>
  <NuxtLink
    ref="containerRef" :to="`/${elementalType?.name || 'prompt'}/${prompt.url}`" flex relative justify-center
    items-center break-all bg-transparent hover:z-2 border-1 border-transparent border-b-gray-lighter hover:rounded-2
    hover:border-gray-lighter :min-h="variant === 'default' ? '32' : 'auto'"
    :pt="(variant === 'default' || variant === 'in-list') && !isMobile ? 5 : variant === 'small' ? 3 : 4"
    :pb="(variant === 'default' || variant === 'in-list') ? 4 : 3"
    :px="(isMobile || variant === 'default' || variant === 'in-list') ? '10px' : 3" :z="savePopperShown ? '!2' : 1"
    class="group" :class="[
      $style.pCard,
      $style[`card-${variant}`],
      lastItem && 'border-b-transparent hover:border-b-gray-lighter',
    ]"
    :style="{ paddingTop: ((variant === 'default' || variant === 'in-list') && !isMobile) ? '20px' : (variant === 'small' ? '12px' : '16px') }"
    @click.stop
  >
    <div flex="~ col" flex-grow :gap="`${prompt.typeId === PROMPT_TYPES.Text ? '1' : '2'}`">
      <div flex gap-2 flex-grow>
        <div flex flex-col flex-grow gap="6px">
          <div flex gap="10px">
            <img
              v-if="(variant === 'default' || variant === 'in-list') && optimizedPromptAvatar"
              :src="optimizedPromptAvatar" :width="avatarSize" :height="avatarSize" h-max rounded-2 b-1 border-soft-gray
              bg-white :title="`${prompt.title} prompt avatar`" :alt="`${prompt.title} prompt avatar`"
            >

            <div
              flex flex-col :gap="variant === 'in-list' ? '6px' : undefined"
              :justify="variant === 'in-list' ? 'center' : undefined"
            >
              <div flex-col :gap="variant === 'default' && !isMobile ? '6px' : '2px'">
                <div v-if="variant !== 'in-list'" flex gap-1>
                  <Text variant="caption" :color="colors.bluishDarker" :max-lines="1">
                    <template v-for="(topicId, index) in prompt.topicIds?.slice(0, 3)" :key="index">
                      <span v-if="index !== 0"> / </span>
                      <NuxtLink
                        v-if="!isMobile" :to="`/topic/${topics?.find(t => t.id === topicId)?.url}`"
                        @click.prevent.stop
                      >
                        {{ topics?.find(t => t.id === topicId)?.name }}
                      </NuxtLink>
                      <span v-else>
                        {{ topics?.find(t => t.id === topicId)?.name }}
                      </span>
                    </template>
                  </Text>

                  <template v-if="prompt.isFeatured && showFeaturedTag">
                    <Text variant="caption" :color="colors.bluishDarker">
                      •
                    </Text>
                    <Text variant="caption" bold :is-gradient-color="true">
                      Featured
                    </Text>
                  </template>
                </div>
                <Heading
                  v-if="variant === 'default' || variant === 'in-list'" level="5-light" :max-lines="2"
                  :color="colors.softBlack" :title="prompt.title"
                >
                  {{ prompt.title }}
                </Heading>

                <Text v-else variant="small" bold :max-lines="1" :color="colors.softBlack">
                  {{ prompt.title }}
                </Text>
              </div>

              <Text
                v-if="variant === 'default' && !isMobile" variant="small" :max-lines="1"
                :color="colors.bluishDarker" mt="2px" break-words whitespace-preline
              >
                <div v-html="stripHTMLDescription(prompt.description)" />
              </Text>

              <section v-if="variant === 'in-list' && !isMobile" flex="~ wrap" items-center gap-4 justify-start>
                <div flex items-center gap-2>
                  <div v-if="prompt.isPrivate" :class="$style.footerItem">
                    <div i-bx:hide text="12px" text-bluish-darker />
                  </div>

                  <UserChip v-if="prompt.user" :user="prompt.user" :class="[!prompt.isPrivate && '-ml-1']" />
                </div>

                <div flex="~ wrap" items-center gap-4>
                  <div v-if="typeof prompt.nUses === 'number'" :class="$style.footerItem">
                    <div i-ic:outline-arrow-outward font-size-12px text-bluish-darker />

                    <Text variant="caption" text-bluish-darker bold>
                      {{ formatThousands(prompt.nUses) }}
                    </Text>
                  </div>

                  <div v-if="typeof prompt.nSaves === 'number'" :class="[$style.footerItem]">
                    <div i-material-symbols:bookmark-outline text-bluishDarker />

                    <Text variant="caption" text-bluish-darker bold>
                      {{ formatThousands(prompt.nSaves) }}
                    </Text>
                  </div>

                  <div v-if="prompt.createdAt" :class="$style.footerItem">
                    <Text variant="caption" text-bluish-darker>
                      {{ formatTimeAgo(new Date(prompt.createdAt)) }}
                    </Text>
                  </div>

                  <TextV2
                    small
                    class="flex items-center gap-0.5 text-darkGray border-softGray border-1 text-center rounded px-2 flex-shrink-0 !text-[10px] !leading-[20px]"
                  >
                    <span>
                      <div v-if="isList" i-mdi:folder-outline />
                      <div v-else-if="elementalType?.name === 'snippet'" i-mdi:slash-forward />
                      <div v-else-if="elementalType?.name === 'automation'" i-material-symbols-light:bolt-outline />
                      <div v-else-if="elementalType?.name === 'document'" i-mdi:file-outline />
                      <IconStar v-else-if="elementalType?.name === 'prompt'" stroke-color="currentColor" :size="10" />
                    </span>
                    <span>{{ `${capitalizeFirstLetter(elementalType?.name)}${(isList && !elementalType?.name.match(/list/i) ? 'List' : '')}` }}</span>
                  </TextV2>
                </div>
              </section>
            </div>
          </div>
        </div>

        <div class="flex flex-col gap-2 flex-shrink-0 items-end">
          <ClientOnly v-if="variant === 'default'">
            <PromptUpvoteButton v-if="showUpvoteButton" variant="mini" :prompt="prompt" />
          </ClientOnly>
          <ClientOnly v-if="variant === 'in-list'">
            <div invisible group-hover:visible class="<md:visible">
              <PromptUpvoteButton variant="mini" :prompt="prompt" />
            </div>
          </ClientOnly>
          <div
            v-else-if="variant === 'small'" flex w-0 opacity-0 overflow-hidden group-hover:opacity-100
            group-hover:w-auto gap-1 :class="$style.actionButtons"
          >
            <ClientOnly>
              <PromptCopy :prompt="prompt" variant="mini" />
            </ClientOnly>

            <ElementalSave
              v-if="elementalType" :element="prompt as Elemental" :elemental-type="elementalType"
              variant="mini" @popper-toggled="handleSavePopperToggle"
            />
          </div>
        </div>
      </div>

      <div
        v-if="variant === 'default' && prompt.typeId === PROMPT_TYPES.Visual && promptImages?.length && promptImageSizes"
        block w-full
      >
        <section grid :class="$style[`gridCols${promptImages.length}`]">
          <template v-if="promptImages.length < 3">
            <div
              v-for="(image, index) in promptImages" :key="image.id" :w="`${promptImageSizes[index]}px`"
              :h="`${promptImageSizes[index]}px`"
            >
              <img
                :src="optimizeImage(image.url, {
                  width: promptImageSizes[index] * 2, height: promptImageSizes[index] * 2,
                })" b-1 border-white
                :rounded="promptImages.length === 1 ? '2' : (index === 0 ? 'bl-2 tl-2' : 'br-2 tr-2')"
                :alt="prompt.description" :title="prompt.title" :data-pin-description="prompt.description"
                :data-pin-media="image.url"
              >
            </div>
          </template>
          <template v-else>
            <img
              :src="optimizeImage(promptImages[0].url, {
                width: promptImageSizes[0] * 2, height: promptImageSizes[0] * 2,
              })" b-1 border-white rounded="bl-2 tl-2" :alt="prompt.description" :title="prompt.title"
              :data-pin-description="prompt.description" :data-pin-media="promptImages[0].url"
            >
            <div grid grid-rows-auto>
              <template v-for="(image, index) in promptImages">
                <img
                  v-if="index > 0" :key="index" :src="optimizeImage(image.url, {
                    width: promptImageSizes[index] * 2, height: promptImageSizes[index] * 2,
                  })" b-1 border-white
                  :rounded="index === 1 ? 'tr-2' : (index === (promptImages.length - 1) ? 'br-2' : '0')"
                  :alt="prompt.description" :title="prompt.title" :data-pin-description="prompt.description"
                  :data-pin-media="image.url"
                >
              </template>
            </div>
          </template>
        </section>
      </div>

      <div v-if="variant !== 'in-list' || isMobile" flex justify-between items-center>
        <section
          flex="~ wrap" flex-grow items-center :gap="(isMobile || variant === 'small') ? 3 : 4"
          :justify="variant === 'small' ? 'between' : 'start'"
        >
          <div flex items-center gap-2>
            <div v-if="prompt.isPrivate" :class="$style.footerItem">
              <div i-bx:hide text="12px" text-bluish-darker />
            </div>

            <UserChip v-if="prompt.user" :user="prompt.user" :class="[!prompt.isPrivate && '-ml-1']" />
          </div>

          <div flex="~ wrap" items-center :gap="isMobile ? 3 : 4">
            <div v-if="typeof prompt.nUses === 'number'" :class="$style.footerItem">
              <div i-ic:outline-arrow-outward font-size-12px text-bluish-darker />

              <Text variant="caption" text-bluish-darker bold>
                {{ formatThousands(prompt.nUses) }}
              </Text>
            </div>

            <div v-if="typeof prompt.nSaves === 'number'" :class="$style.footerItem">
              <div i-material-symbols:bookmark-outline text-bluishDarker />

              <Text variant="caption" text-bluish-darker bold>
                {{ formatThousands(prompt.nSaves) }}
              </Text>
            </div>

            <div v-if="variant === 'default' && prompt.createdAt" :class="$style.footerItem">
              <Text variant="caption" text-bluish-darker>
                {{ formatTimeAgo(new Date(prompt.createdAt)) }}
              </Text>
            </div>

            <TextV2
              small
              class="flex items-center gap-0.5 text-darkGray border-softGray border-1 text-center rounded px-2 flex-shrink-0 !text-[10px] !leading-[20px]"
            >
              <span>
                <div v-if="elementalType?.name === 'list'" i-mdi:folder-outline />
                <div v-else-if="elementalType?.name === 'snippet'" i-mdi:slash-forward />
                <div v-else-if="elementalType?.name === 'automation'" i-material-symbols-light:bolt-outline />
                <div v-else-if="elementalType?.name === 'document'" i-mdi:file-outline />
                <IconStar v-else-if="elementalType?.name === 'prompt'" stroke-color="currentColor" :size="10" />
              </span>
              <span>{{ capitalizeFirstLetter(elementalType?.name) }}</span>
            </TextV2>
          </div>
        </section>

        <div
          v-if="variant === 'default'" flex w-0 opacity-0 overflow-hidden group-hover:opacity-100 group-hover:w-auto
          gap-1 class="<md:w-auto <md:opacity-100" :class="!isMobile && $style.actionButtons"
        >
          <ClientOnly>
            <PromptCopy :prompt="prompt" variant="mini" />
          </ClientOnly>

          <ElementalSave
            v-if="elementalType" :element="prompt as Elemental" :elemental-type="elementalType"
            variant="mini" @popper-toggled="handleSavePopperToggle"
          />
        </div>
      </div>
    </div>
  </NuxtLink>
</template>

<style module lang="scss">
.card-default::before {
  position: absolute;
  content: '';
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  border-radius: 8px;
  background-image: linear-gradient(226deg, #F6F6FF 0%, rgba(246, 246, 255, 0.00) 100%);
  z-index: -1;
  transition: opacity 0.35s linear;
  opacity: 0;
}

.card-default:hover {
  z-index: 2;

  &::before {
    opacity: 1;
  }
}

.card-in-list {
  @apply bg-gray-lighter rounded-2;
}

.actionButtons {
  transition: opacity .35s linear;
}

.footerItem {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
}

.saved {
  @apply bg-primary text-white;
}

.notSaved {
  @apply bg-light-bluish text-bluish-softer;
  @apply hover: (bg-primary-light text-primary);
}

.gridCols1 {
  grid-template-columns: repeat(1, minmax(0, 1fr));
}

.gridCols2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.gridCols3 {
  grid-template-columns: 2fr 1fr;
}

.gridCols4 {
  grid-template-columns: 3fr 1fr;
}
</style>
